import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  Alert,
  Badge,
  Button,
  Card,
  CaseGateCard,
  Cluster,
  PageHeading,
  ReviewCard,
  Stack,
  Table,
  TaskTable,
  useCaseTaskIssue,
} from "~/components";
import { reportActionError } from "~/lib/actionErrors";
import { queryClient } from "~/lib/queryClient";
import { formatDateTime } from "~/lib/dates";
import { useResolveException } from "~/lib/documents";
import { caseTasksQueryOptions } from "~/lib/cases";
import {
  documentStatus,
  documentStatusVariant,
  documentUploadQueryKey,
  documentUploadQueryOptions,
  droppedDocuments,
  isSettling,
  leafDocuments,
  markSettling,
  NO_SETTLING,
  pruneSettled,
  reviewQueue,
  SETTLE_POLL_MS,
  shouldPollSettling,
  uploadStatus,
  uploadStatusVariant,
  type DocumentUpload,
  type SettlingDocuments,
  type UploadDocument,
  type UploadDocumentCase,
} from "~/lib/documentUploads";
import {
  gateVerdict,
  isOpenReviewGate,
  openMatchGate,
  tasksListQueryKey,
  tasksListQueryOptions,
  type Task,
  type TasksCollection,
  type Verdict,
} from "~/lib/tasks";
import { postTaskStateEvent, type EventState } from "~/lib/events";

import * as css from "~/routes/_app.document-uploads.$id.css";

// A document upload is a Project like a case, so its detail page is the case
// detail page's twin: load the upload to resolve its project_id, list that
// project's tasks, and render them with the shared TaskTable. The route param
// is the upload's own id (REST).
//
// Since TMF-357 the page is also where the upload's documents get REVIEWED, not
// just linked from: the default Documents tab is a step-by-step queue that
// presents one document at a time with its live gate — the upload's own
// match exception, or (for a case-attached document) the very same gate card
// the case page renders, driven by the very same mutation. The Tasks tab keeps
// the raw project task table.
export const Route = createFileRoute("/_app/document-uploads/$id")({
  component: DocumentUploadDetailPage,
  loader: async ({ params }) => {
    const upload = await queryClient.ensureQueryData(
      documentUploadQueryOptions(params.id),
    );
    await queryClient.ensureQueryData(
      tasksListQueryOptions({ project_id: upload.project_id }),
    );
  },
});

/** The two panes of the detail page. Closed union — there is no third tab. */
type UploadTab = "documents" | "tasks";

function DocumentUploadDetailPage() {
  const { id } = Route.useParams();
  const queryClientInstance = useQueryClient();
  // Documents the human has answered but whose rows the engine hasn't rewritten
  // yet. See the settle-tracking block in lib/documentUploads.ts for why the
  // queue can't just invalidate once and wait.
  const [settling, setSettling] = useState<SettlingDocuments>(NO_SETTLING);
  // Both halves of the queue poll on the same condition, because both halves
  // feed it: the upload carries the document rows, the tasks carry the gate
  // states the queue derives reviewability from (TMF-371). Polling only the
  // upload froze the tasks cache on the `pending` gate the reviewer had just
  // answered — the engine's own eager mark — and the queue kept re-presenting
  // it. Each query reads its counterpart from the cache: the poll runs outside
  // render, so it can't close over the other query's binding.
  const settlePoll = (
    upload: DocumentUpload | undefined,
    tasks: Task[],
  ): number | false => {
    if (upload === undefined) return false;
    const outstanding = pruneSettled(settling, reviewQueue(upload, tasks));
    return shouldPollSettling(outstanding, Date.now()) ? SETTLE_POLL_MS : false;
  };
  const cachedTasks = (uploadProjectId: string): Task[] =>
    queryClientInstance.getQueryData<TasksCollection>(
      tasksListQueryKey({ project_id: uploadProjectId }),
    )?._embedded ?? [];
  const uploadQuery = useQuery({
    ...documentUploadQueryOptions(id),
    // Function form: the interval re-evaluates against the freshest data every
    // tick, so the poll switches itself off as soon as the last settling
    // document lands (or times out). No effect, no cleanup to leak.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data === undefined) return false;
      return settlePoll(data, cachedTasks(data.project_id));
    },
  });
  const projectId = uploadQuery.data?.project_id ?? "";
  const tasksFilter = { project_id: projectId };
  const tasksQuery = useQuery({
    ...tasksListQueryOptions(tasksFilter),
    enabled: projectId !== "",
    refetchInterval: (query) =>
      settlePoll(
        queryClientInstance.getQueryData<DocumentUpload>(
          documentUploadQueryKey(id),
        ),
        query.state.data?._embedded ?? [],
      ),
  });
  const resolve = useResolveException();
  const [tab, setTab] = useState<UploadTab>("documents");

  // Manual task-state changes on this project (Mark done / Reject / Reopen).
  // The set filevine project id gate goes through resolve() instead.
  const issue = useMutation({
    mutationFn: ({ taskId, state }: { taskId: string; state: EventState }) =>
      postTaskStateEvent({ projectId, taskId, state }),
    onSuccess: () =>
      queryClientInstance.invalidateQueries({ queryKey: tasksListQueryKey(tasksFilter) }),
    // Without this the row just never changed — a rejection read as a broken
    // button (TMF-362).
    onError: (error) => reportActionError(error),
  });

  // Freshness after a queue gate action. Two halves:
  //
  //   - The document is marked SETTLING, which immediately swaps its slot for
  //     the quiet working state and starts the poll above. This is what makes
  //     the queue advance: the action itself changes no document row, so
  //     without it the answered document would stay focused forever.
  //   - The upload detail is invalidated so the first refetch happens now
  //     rather than one poll tick later.
  //
  // The CASE's tasks need nothing from here. useChangeTaskState's onSuccess
  // already invalidates CASES_LIST_KEY (["cases"]), and React Query matches by
  // prefix — that reaches ["cases", caseId, "tasks"] and actively refetches it.
  // What keeps the refetch from resurrecting the Approve button is server-side:
  // POST /api/events synchronously flips the target task to `pending` before it
  // returns (eagerMarkPendingForEvent, api/app/services/projects/service.go),
  // so the refetch reads `pending` — not a stale `todo` — and the card renders
  // read-only until the consumer lands the real state.
  const beginSettling = (document: UploadDocument) => {
    setSettling((current) => markSettling(current, document, Date.now()));
    void queryClientInstance.invalidateQueries({
      queryKey: documentUploadQueryKey(id),
    });
  };

  const heading = (
    <PageHeading
      title="Document Upload"
      subtitle="Review the documents this upload dropped, and clear anything that needs a human."
      actions={
        <Link to="/document-uploads">
          <Button type="button" variant="secondary">
            Back to uploads
          </Button>
        </Link>
      }
    />
  );

  if (uploadQuery.isPending || tasksQuery.isPending) {
    return (
      <Stack gap="lg">
        {heading}
        <Card>
          <p>Loading tasks…</p>
        </Card>
      </Stack>
    );
  }

  if (uploadQuery.isError || tasksQuery.isError) {
    return (
      <Stack gap="lg">
        {heading}
        <Card accent="danger">
          <p>Couldn't load this upload.</p>
        </Card>
      </Stack>
    );
  }

  const tasks = tasksQuery.data?._embedded ?? [];
  const documents = uploadQuery.data ? droppedDocuments(uploadQuery.data) : [];
  const leaves = uploadQuery.data ? leafDocuments(uploadQuery.data) : [];
  const queue = uploadQuery.data ? reviewQueue(uploadQuery.data, tasks) : [];
  // Derived, never stored: a settling document drops out the moment its row
  // moves, with no state-sync effect to fall out of step with the data.
  const outstanding = pruneSettled(settling, queue);
  const status = uploadQuery.data ? uploadStatus(leaves) : "Processing";

  return (
    <Stack gap="lg">
      {heading}
      <Card>
        <Cluster gap="xl" align="center">
          <span>
            <strong>Uploaded</strong> {formatDateTime(uploadQuery.data?.created_at)}
          </span>
          <span>
            <strong>Documents</strong> {documents.length}
          </span>
          <span>
            <strong>Status</strong>{" "}
            <Badge variant={uploadStatusVariant(status)}>{status}</Badge>
          </span>
        </Cluster>
      </Card>

      {/* Minimal two-button tab header — no tab precedent existed in the app,
          and a queue that needs one switch doesn't justify inventing a tab
          primitive. Reuses the Button variants as the selected/unselected
          pair, so it inherits the one design system unchanged. */}
      <Cluster gap="sm">
        <Button
          type="button"
          variant={tab === "documents" ? "primary" : "secondary"}
          onClick={() => setTab("documents")}
          aria-pressed={tab === "documents"}
        >
          Documents
        </Button>
        <Button
          type="button"
          variant={tab === "tasks" ? "primary" : "secondary"}
          onClick={() => setTab("tasks")}
          aria-pressed={tab === "tasks"}
        >
          Tasks
        </Button>
      </Cluster>

      {tab === "documents" ? (
        <>
          <ReviewQueue
            queue={queue}
            settling={outstanding}
            tasks={tasks}
            resolve={resolve}
            onActed={beginSettling}
          />
          <Card>
            <Stack gap="sm">
              <h2>All documents</h2>
              {leaves.length === 0 ? (
                <p>No documents yet — this upload is still being split.</p>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Case</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaves.map((document) => (
                      <DocumentRow key={document.id} document={document} />
                    ))}
                  </tbody>
                </Table>
              )}
            </Stack>
          </Card>
        </>
      ) : (
        <Card>
          <Stack gap="sm">
            <h2>Tasks</h2>
            <TaskTable
              tasks={tasks}
              onIssue={(task: Task, state: EventState) =>
                issue.mutate({ taskId: task.id, state })
              }
              pendingTaskId={issue.isPending ? issue.variables?.taskId : undefined}
              pendingState={issue.isPending ? issue.variables?.state : undefined}
              onResolveGate={(task, filevineProjectID) =>
                resolve.mutate({
                  documentId: task._embedded.document?.id ?? "",
                  matchedProjectId: filevineProjectID,
                })
              }
              resolvePending={resolve.isPending}
            />
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

// ── Review queue ─────────────────────────────────────────────────────
//
// The step-by-step disposition of everything on this upload that needs a
// person (TMF-357). One document at a time, its live gate rendered in place:
// the reviewer never has to work out WHICH of the upload's documents the open
// task belongs to, and never gets sent to a case page to finish the job.

type ResolveExceptionMutation = ReturnType<typeof useResolveException>;

interface ReviewQueueProps {
  /** The documents needing a human, in queue order (documentUploads.reviewQueue). */
  queue: UploadDocument[];
  /** Documents already answered, still waiting on the engine to rewrite their
   *  rows — rendered as the quiet working state, never as a stale gate card. */
  settling: SettlingDocuments;
  /** The upload project's tasks (already loaded by the page) — the gate a
   *  document is held by, and therefore whether it is reviewable at all. */
  tasks: Task[];
  resolve: ResolveExceptionMutation;
  /** A gate action landed on this document — start settling it. */
  onActed: (document: UploadDocument) => void;
}

function ReviewQueue({
  queue,
  settling,
  tasks,
  resolve,
  onActed,
}: ReviewQueueProps) {
  const [position, setPosition] = useState(0);

  if (queue.length === 0) {
    return (
      <Card>
        <Stack gap="sm">
          <h2>Review</h2>
          <p className={css.queueMuted}>
            Nothing needs review — every document here is filed or still working
            its way through.
          </p>
        </Stack>
      </Card>
    );
  }

  // Clamp rather than store a corrected index: the queue shrinks underneath the
  // reviewer as documents file, and a render-time clamp can't get out of sync
  // with the data the way a state-sync effect can.
  const index = Math.min(position, queue.length - 1);
  const document = queue[index];
  if (document === undefined) return null;

  // The failure belongs to the document it happened on — clear it on the way to
  // another one, or the danger banner follows the reviewer down the queue.
  const moveTo = (next: number) => {
    resolve.reset();
    setPosition(next);
  };

  return (
    <Card>
      <Stack gap="md">
        <Cluster gap="sm" align="center">
          <h2>Review</h2>
          <span className={css.queuePosition}>
            Document {index + 1} of {queue.length}
          </span>
          <span className={css.queueDocName}>
            {document.name || "(unnamed document)"}
          </span>
          <span className={css.queueNav}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => moveTo(index - 1)}
              disabled={index === 0}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => moveTo(index + 1)}
              disabled={index >= queue.length - 1}
            >
              Next
            </Button>
          </span>
        </Cluster>

        {/* Which document failed, not why — the reason is the server's own, and
            useResolveException reports it through the app-wide action-error
            toast (TMF-362). Naming a cause here would guess at one the toast
            may contradict. This anchors the toast to the card the reviewer is
            looking at, since the queue moves under them.

            Deliberately silent on what did or didn't persist, too: a refusal
            writes nothing, but a failure of the last step (publishing the
            completion) leaves the case assigned with the gate still open.
            Resolving again is the right move either way. */}
        {resolve.isError ? (
          <Alert variant="danger">
            This document's case assignment didn't go through. Check the message
            for the reason, then try again.
          </Alert>
        ) : null}

        {/* A named region: the focused document's gate is no longer a table
            row, so this is what scopes "the card the reviewer is looking at"
            for assistive tech and for the e2e locators. */}
        <section aria-label="Review queue">
          <QueueGate
            key={document.id}
            document={document}
            settling={isSettling(settling, document.id)}
            tasks={tasks}
            resolve={resolve}
            onActed={onActed}
          />
        </section>
      </Stack>
    </Card>
  );
}

/** The quiet state: the machine has this document, the human doesn't. Used for
 *  a document the reviewer just answered (settling), for the match/sync windows,
 *  and for a case document with no gate open — anywhere a gate card would
 *  otherwise sit there stale or dead. Links to the case whenever there is one,
 *  so following the work is always one click away. */
function QueueWorkingState({
  matchedCase,
}: {
  matchedCase: UploadDocumentCase | null | undefined;
}) {
  if (matchedCase == null) {
    return (
      <p className={css.queueMuted}>
        The system is working on this document.
      </p>
    );
  }
  return (
    <p className={css.queueMuted}>
      The system is working on this document on{" "}
      <Link to="/cases/$id" params={{ id: matchedCase.id }}>
        this case
      </Link>
      .
    </p>
  );
}

/** Route the focused document to the gate that is actually holding it. The two
 *  arms are separate components so each owns its own hooks unconditionally. */
function QueueGate({
  document,
  settling,
  tasks,
  resolve,
  onActed,
}: {
  document: UploadDocument;
  settling: boolean;
  tasks: Task[];
  resolve: ResolveExceptionMutation;
  onActed: (document: UploadDocument) => void;
}) {
  const matchedCase = document._embedded?.case;
  // Answered, waiting on the engine. Checked FIRST: the gate task this document
  // was holding is still open on the server for a few more seconds, and
  // re-rendering its card (buttons stripped, or worse, still live) is exactly
  // the freeze this queue exists to avoid.
  if (settling) return <QueueWorkingState matchedCase={matchedCase} />;

  // Reviewability is the GATE's state, not the document's metadata (TMF-371).
  // The verdict is resolved here too, so the card can require one rather than
  // carry a branch for a case it can never be handed.
  const gate = openMatchGate(tasks, document.id);
  const verdict = gate === undefined ? undefined : gateVerdict(gate);
  if (gate !== undefined && verdict !== undefined) {
    return (
      <MatchExceptionGate
        document={document}
        gate={gate}
        verdict={verdict}
        resolve={resolve}
        onResolved={() => onActed(document)}
      />
    );
  }
  if (documentStatus(document) === "Needs match") {
    // Unmatched with no gate open yet: the match consumer is still running (the
    // TODO(TMF-360) approximation this queue inherits). Never a dead card.
    // https://linear.app/bitstorm-technologies/issue/TMF-360/engine-driven-status-projection-filterable-denormalized-status-on
    return <QueueWorkingState matchedCase={null} />;
  }
  if (matchedCase == null) {
    // Awaiting case review with no case embed yet — the sync window.
    return <QueueWorkingState matchedCase={null} />;
  }
  return (
    <CaseReviewGate
      document={document}
      matchedCase={matchedCase}
      onCompleted={() => onActed(document)}
    />
  );
}

/** "Needs match": the upload's OWN `set filevine project id` exception gate,
 *  resolved with the exact affordance (and mutation) the Tasks tab uses. */
function MatchExceptionGate({
  document,
  gate,
  verdict,
  resolve,
  onResolved,
}: {
  document: UploadDocument;
  gate: Task;
  verdict: Verdict;
  resolve: ResolveExceptionMutation;
  onResolved: () => void;
}) {
  return (
    <ReviewCard
      task={gate}
      verdict={verdict}
      isException
      // openMatchGate hands over only gates awaiting a human, so a card that
      // renders here is always answerable.
      actionable
      // Never called for an exception gate — the card renders the Filevine-id
      // input instead of Approve/Reject.
      onApprove={() => {}}
      onReject={() => {}}
      onResolve={(filevineProjectID) =>
        resolve.mutate(
          {
            documentId: document.id,
            matchedProjectId: filevineProjectID,
          },
          { onSuccess: onResolved },
        )
      }
      inFlight={false}
      inFlightState={undefined}
      resolvePending={resolve.isPending}
    />
  );
}

/** "Awaiting case review": the document is on a case, and the gate holding it
 *  is one of that case's review tasks. Render the case page's own gate card in
 *  place rather than sending the reviewer away. Reads the CASE's tasks through
 *  caseTasksQueryOptions — deliberately the same cache entry the case detail
 *  page uses, so the two surfaces can never show different gate state. */
function CaseReviewGate({
  document,
  matchedCase,
  onCompleted,
}: {
  document: UploadDocument;
  matchedCase: UploadDocumentCase;
  onCompleted: () => void;
}) {
  const tasksQuery = useQuery(
    caseTasksQueryOptions(matchedCase.id, matchedCase.project_id),
  );
  // The gate mutation is owned HERE, not inside CaseGateCard: approving
  // optimistically flips the gate to `complete`, the gate lookup below then
  // finds nothing and swaps the card for the working state — unmounting the card
  // mid-POST, which drops the mutate()-scoped onSuccess that marks the document
  // settling and freezes the queue (TMF-363). This component survives the flip
  // (open → working → open), so the callback fires. Same shape as the exception
  // arm, where `resolve` is owned above MatchExceptionGate.
  const caseTask = useCaseTaskIssue(
    matchedCase.id,
    matchedCase.project_id,
    onCompleted,
  );

  if (tasksQuery.isPending) {
    return <p className={css.queueMuted}>Loading this document's review…</p>;
  }
  if (tasksQuery.isError) {
    return (
      <p className={css.queueMuted}>
        Couldn't load this document's review — open{" "}
        <Link to="/cases/$id" params={{ id: matchedCase.id }}>
          this case
        </Link>{" "}
        to work it.
      </p>
    );
  }

  const gate = (tasksQuery.data?.tasks ?? []).find(
    (t) =>
      t._embedded.document?.id === document.id &&
      isOpenReviewGate(t) &&
      // Queue-only narrowing: a `pending` gate is one POST /api/events already
      // accepted and the engine is mid-transition on, so for this queue "the
      // machine has it" — render the working state, never a card whose buttons
      // are stripped. Keeps a lost settle-mark degrading gracefully instead of
      // dead-ending the reviewer (TMF-363). The case page's own read of
      // isOpenReviewGate is untouched: there a read-only row is the right answer.
      t.state !== "pending",
  );
  if (gate === undefined) {
    // No open gate: the engine has automated work in flight (or just finished
    // it). Say so quietly and offer the case — never a dead card.
    return <QueueWorkingState matchedCase={matchedCase} />;
  }

  return <CaseGateCard task={gate} caseTask={caseTask} />;
}

// One leaf document of the upload. The status chip uses the same vocabulary as
// the upload badge, so a document reading "Awaiting case review" is visibly the
// reason the whole upload does — and the case link is the way out of the dead
// end that motivated TMF-357: the approve gates live on the CASE page, never
// here. The case embed rides the detail response only (documentUploadsRepo
// preloads Documents.Case on the single-upload load).
function DocumentRow({ document }: { document: UploadDocument }) {
  const status = documentStatus(document);
  const matchedCase = document._embedded?.case;
  return (
    <tr>
      <td>{document.name || "(unnamed document)"}</td>
      <td>{document.type}</td>
      <td>
        <Badge variant={documentStatusVariant(status)}>{status}</Badge>
      </td>
      <td>
        {matchedCase ? (
          <Link to="/cases/$id" params={{ id: matchedCase.id }}>
            View case →
          </Link>
        ) : (
          "—"
        )}
      </td>
    </tr>
  );
}
